import { useMemo } from 'react';
import { IconButton } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Table } from '@/components';
import { ColumnConfig } from '@/components/table/Table';
import { PickupCycle } from '@/pages/Business/pages/PickupCycles/models';
import { PickupCycleRow } from '@/pages/Business/pages/PickupCycles/utils/rows';
import {
  COUNTRY,
  COUNTRY_LABEL,
  OTHER_COUNTRY,
  wallClockInCountry,
} from '@/config/country';

interface PickupCyclesTableProps {
  rows: PickupCycleRow[];
  isLoading: boolean;
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onEdit: (cycle: PickupCycle) => void;
  onDelete: (id: string) => void;
  onConfigureAddress: (branchId: string) => void;
}

const PickupCyclesTable = ({
  rows,
  isLoading,
  totalItems,
  totalPages,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onConfigureAddress,
}: PickupCyclesTableProps) => {
  const columns: ColumnConfig[] = useMemo(
    () => [
      { label: 'Comercio', field: 'commerceName' },
      { label: 'Sucursal', field: 'branchLabel' },
      {
        label: 'Dirección',
        field: 'addressText',
        render: (_value, row: PickupCycleRow) => {
          if (!row.addressMissing && row.addressText) {
            return <span className='text-gray-700'>{row.addressText}</span>;
          }

          return (
            <div className='flex flex-col gap-1'>
              <span className='text-red-600 font-medium'>
                ⚠️ Sin dirección configurada
              </span>
              {row.configurableBranchId && (
                <button
                  type='button'
                  className='text-left text-sm text-[var(--primary-color)] underline hover:opacity-80'
                  onClick={() =>
                    onConfigureAddress(row.configurableBranchId as string)
                  }
                >
                  Configurar dirección
                </button>
              )}
            </div>
          );
        },
      },
      {
        label: `Vuelta (hora de ${COUNTRY_LABEL[COUNTRY]})`,
        field: 'timeLabel',
        /*
         * La hora del ciclo es hora de pared del pais donde corre el back, y se
         * muestra sin convertir. Quien mira desde el otro pais necesita las dos
         * cosas: la hora con la que se configura la vuelta, y la hora en la que
         * la va a ver pasar en su propio reloj.
         */
        render: (value: string) => {
          if (!value) return '—';
          const there = wallClockInCountry(value, COUNTRY, OTHER_COUNTRY);
          return (
            <div className='flex flex-col'>
              <span>{value}</span>
              {there && (
                <span className='text-xs text-gray-500'>
                  {there} en {COUNTRY_LABEL[OTHER_COUNTRY]}
                </span>
              )}
            </div>
          );
        },
      },
      {
        label: 'Días',
        field: 'daysLabel',
        render: (value: string) =>
          value === '⚠️ Sin días' ? (
            <span className='text-red-600 font-medium'>{value}</span>
          ) : (
            value
          ),
      },
      { label: 'Etiqueta', field: 'tagLabel' },
    ],
    [onConfigureAddress],
  );

  const renderActions = (row: PickupCycleRow) => (
    <div className='flex items-center gap-2'>
      <IconButton
        aria-label='Editar retiro'
        onClick={() => onEdit(row.cycle)}
        size='small'
        color='primary'
      >
        <EditIcon fontSize='small' />
      </IconButton>
      <IconButton
        aria-label='Borrar retiro'
        onClick={() => {
          if (window.confirm('¿Seguro que querés borrar este retiro?')) {
            onDelete(row.id);
          }
        }}
        size='small'
        color='error'
      >
        <DeleteIcon fontSize='small' />
      </IconButton>
    </div>
  );

  return (
    <div className='w-full bg-white rounded-lg shadow-md overflow-hidden'>
      <Table
        columns={columns}
        data={rows}
        renderActions={renderActions}
        isLoading={isLoading}
        pagination={{
          totalItems,
          totalPages,
          currentPage,
          rowsPerPage: pageSize,
          onPageChange,
          onRowsPerPageChange: onPageSizeChange,
          rowsPerPageOptions: [25, 50, 100],
        }}
      />
    </div>
  );
};

export default PickupCyclesTable;
